import { useState } from "react"

import type { FilePickerSource, NavigationItem, SharePointLibrary, SharePointSite } from "../types"
import type { GraphFileItem } from "@/api/models"

export function useFilePickerNavigation(initialSource: FilePickerSource = "local") {
  const [tab, setTab] = useState<FilePickerSource>(initialSource)
  const [navStack, setNavStack] = useState<NavigationItem[]>([])
  const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null)
  const [selectedLibrary, setSelectedLibrary] = useState<SharePointLibrary | null>(null)

  const navigateToFolder = (folder: GraphFileItem) => {
    if (folder.isFolder && folder.driveId && folder.id) {
      setNavStack((prev) => [
        ...prev,
        {
          driveId: folder.driveId,
          itemId: folder.id,
          name: folder.name,
          lastModified: folder.lastModified,
        },
      ])
    }
  }

  const navigateBack = () => {
    if (tab === "oneDrive" && navStack.length > 0) {
      setNavStack((prev) => prev.slice(0, -1))
    } else if (tab === "sharePoint" && selectedSite && selectedLibrary) {
      if (navStack.length > 0) {
        setNavStack((prev) => prev.slice(0, -1))
      } else {
        setSelectedLibrary(null)
      }
    }
  }

  const selectSite = (site: SharePointSite) => {
    setSelectedSite(site)
    setNavStack([])
    setSelectedLibrary(null)
  }

  const selectLibrary = (library: SharePointLibrary) => {
    setSelectedLibrary(library)
    setNavStack([])
  }

  const resetNavigation = () => {
    setNavStack([])
    setSelectedSite(null)
    setSelectedLibrary(null)
  }

  const getBreadcrumb = () => {
    if (tab === "oneDrive") {
      return navStack.map((n) => n.name).join(" / ")
    } else if (tab === "sharePoint") {
      const parts = [
        selectedSite?.displayName,
        selectedLibrary?.name,
        ...navStack.map((n) => n.name),
      ]
      return parts.filter(Boolean).join(" / ")
    }
    return ""
  }

  const canNavigateBack = (): boolean => {
    if (tab === "oneDrive") return navStack.length > 0
    if (tab === "sharePoint") return !!(selectedSite && selectedLibrary)
    return false
  }

  return {
    // State
    tab,
    navStack,
    selectedSite,
    selectedLibrary,

    // Actions
    setTab,
    setSelectedSite,
    setSelectedLibrary,
    navigateToFolder,
    navigateBack,
    selectSite,
    selectLibrary,
    resetNavigation,

    // Computed
    getBreadcrumb,
    canNavigateBack,
  }
}
